import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
// Import utilities for safe access and error handling (UNDER THE HOOD IMPROVEMENTS)
import ErrorBoundary from './components/ErrorBoundary';
import Header from './components/Header';
import LandingPage from './components/LandingPage';
import Assessment from './components/Assessment';
import AdminAssessmentResults from './components/AdminAssessmentResults';
import Dashboard from './components/Dashboard';
import ProfessionalReportCheckout from './components/ProfessionalReportCheckout';
import ConversionFunnelDashboard from './components/ConversionFunnelDashboard';
import Projects from './components/Projects';
import ProjectDetail from './components/ProjectDetail';
import ProjectsList from './components/ProjectsList';
import Measures from './components/Measures';
import EMHAAssessment from './components/EMHAAssessment';
import EMHAResults from './components/EMHAResults';
import EMRABooking from './components/EMRABooking';
import EnhancedAssessmentResults from './components/EnhancedAssessmentResults';
import AssessmentCompletionRouter from './components/AssessmentCompletionRouter';
import ESAReportPurchase from './components/ESAReportPurchase';
import SampleESAReport from './components/SampleESAReport';
import PaymentCheckout from './components/PaymentCheckout';
import PaymentSuccess from './components/PaymentSuccess';
import PaymentCancelled from './components/PaymentCancelled';
import MiningPrimaryMetalsLanding from './components/MiningPrimaryMetalsLanding';
import MetalProductsManufacturing from './components/MetalProductsManufacturing';
import FoodBeverageIndustryPage from './components/FoodBeverageIndustryPage';
import PlasticsPackagingIndustryPage from './components/PlasticsPackagingIndustryPage';
import ChemicalsPharmaceuticalsIndustryPage from './components/ChemicalsPharmaceuticalsIndustryPage';
import FacilitiesManagementIndustryPage from './components/FacilitiesManagementIndustryPage';
import ScrollToTop from './components/ScrollToTop';
import ConsultantDashboard from './components/ConsultantDashboard';
import ConsultantSessionPage from './components/ConsultantSessionPage';
import ConsultantInterviewMode from './components/ConsultantInterviewMode';
import ConsultantBrandingSettings from './components/ConsultantBrandingSettings';
import ConsultantClientReports from './components/ConsultantClientReports';
import ConsultantResultsView from './components/ConsultantResultsView';
import ConsultantTrainingHub from './components/ConsultantTrainingHub';
import LiveSessionPage from './components/LiveSessionPage';
import ComprehensiveAssessmentReport from './components/ComprehensiveAssessmentReport';
import AuthPage from './components/AuthPage';
import ProtectedRoute from './components/ProtectedRoute';
import EmailVerification from './components/EmailVerification';
import ForgotPassword from './components/ForgotPassword';
import AdminDashboard from './components/AdminDashboard';
import AdminQuestions from './components/AdminQuestions';
import AdminIndustries from './components/AdminIndustries';
import AdminSubIndustries from './components/AdminSubIndustries';
import ConsultantStatusPage from './components/ConsultantStatusPage';
import OfflineIndicator from './components/OfflineIndicator';
import RecommendationsPage from './components/RecommendationsPage';
import { AppProvider } from './context/AppContext';
import { AuthProvider } from './context/AuthContext';
import { Toaster } from 'react-hot-toast';

// Main App Component
function App() {
  return (
    <ThemeProvider>
    <ErrorBoundary>
      
      <AuthProvider>
        <AppProvider>
          <div className="min-h-screen">
            <Toaster position="top-center" />
            <BrowserRouter>
              <ScrollToTop />
              <OfflineIndicator />
              <Header />
              <Routes>
                <Route path="/" element={<LandingPage />} />
                
                {/* USER ROUTES - Authenticated users only */}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <ErrorBoundary componentName="Dashboard">
                      <Dashboard />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />
                
                {/* Live Session route - Users only */}
                <Route path="/live-session/:sessionId" element={
                  <ProtectedRoute requiredRoles={['user']}>
                    <LiveSessionPage />
                  </ProtectedRoute>
                } />
                
                {/* Assessment routes - Role-based access */}
                <Route path="/assessment/results" element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <ErrorBoundary componentName="AdminAssessmentResults">
                      <AdminAssessmentResults />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/assessment/self-guided" element={
                  <ProtectedRoute requiredRoles={['user']}>
                    <ErrorBoundary componentName="Assessment">
                      <Assessment type="ESA" />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/assessment/professional" element={
                  <ProtectedRoute requiredRoles={['user']}>
                    <ErrorBoundary componentName="Assessment">
                      <Assessment type="ESA" />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/assessment/complete/:assessmentId" element={
                  <ProtectedRoute>
                    <AssessmentCompletionRouter />
                  </ProtectedRoute>
                } />
                <Route path="/assessment/results/:assessmentId" element={
                  <ProtectedRoute>
                    <EnhancedAssessmentResults />
                  </ProtectedRoute>
                } />
                <Route path="/assessments/:assessmentId/recommendations" element={
                  <ProtectedRoute>
                    <ErrorBoundary componentName="RecommendationsPage">
                      <RecommendationsPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />
                
                {/* EMHA routes - Users only */}
                <Route path="/emha" element={
                  <ProtectedRoute requiredRoles={['user']}>
                    <EMHAAssessment />
                  </ProtectedRoute>
                } />
                <Route path="/emha/results/:assessmentId" element={
                  <ProtectedRoute>
                    <EMHAResults />
                  </ProtectedRoute>
                } />
                
                {/* Project routes - Authenticated users only */}
                <Route path="/projects" element={
                  <ProtectedRoute>
                    <ErrorBoundary componentName="ProjectsList">
                      <ProjectsList />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/projects/:projectId" element={
                  <ProtectedRoute>
                    <ErrorBoundary componentName="ProjectDetail">
                      <ProjectDetail />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/projects-legacy" element={
                  <ProtectedRoute>
                    <Projects />
                  </ProtectedRoute>
                } />
                <Route path="/measures" element={
                  <ProtectedRoute>
                    <Measures />
                  </ProtectedRoute>
                } />
                
                {/* Payment routes - Authenticated users only */}
                <Route path="/payment/checkout/:assessmentId?" element={
                  <ProtectedRoute>
                    <PaymentCheckout />
                  </ProtectedRoute>
                } />
                <Route path="/payment/success" element={
                  <ProtectedRoute>
                    <PaymentSuccess />
                  </ProtectedRoute>
                } />
                <Route path="/payment/cancelled" element={
                  <ProtectedRoute>
                    <PaymentCancelled />
                  </ProtectedRoute>
                } />
                
                {/* Additional protected routes */}
                <Route path="/emra/booking" element={
                  <ProtectedRoute>
                    <EMRABooking />
                  </ProtectedRoute>
                } />
                <Route path="/upgrade-to-professional" element={
                  <ProtectedRoute>
                    <ProfessionalReportCheckout />
                  </ProtectedRoute>
                } />
                <Route path="/conversion-funnel" element={
                  <ProtectedRoute>
                    <ConversionFunnelDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/esa-report-purchase" element={
                  <ProtectedRoute>
                    <ESAReportPurchase />
                  </ProtectedRoute>
                } />
                
                {/* PUBLIC ROUTES - No authentication required */}
                <Route path="/sample-esa-report" element={<SampleESAReport />} />
                <Route path="/mining-primary-metals" element={<MiningPrimaryMetalsLanding />} />
                <Route path="/metal-products" element={<MetalProductsManufacturing />} />
                <Route path="/food-beverage" element={<FoodBeverageIndustryPage />} />
                <Route path="/plastics-packaging" element={<PlasticsPackagingIndustryPage />} />
                <Route path="/chemicals-pharmaceuticals" element={<ChemicalsPharmaceuticalsIndustryPage />} />
                <Route path="/facilities-management" element={<FacilitiesManagementIndustryPage />} />

                {/* Authentication Routes - PUBLIC */}
                <Route path="/login" element={<AuthPage />} />
                <Route path="/register" element={<AuthPage />} />
                <Route path="/consultant/login" element={<AuthPage />} />
                <Route path="/verify-email" element={<EmailVerification />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />

                {/* Testing Routes - Removed during cleanup */}

                {/* Admin Dashboard - ADMIN ONLY */}
                <Route path="/admin" element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <AdminDashboard />
                  </ProtectedRoute>
                } />
                
                {/* Admin Question Management - ADMIN ONLY */}
                <Route path="/admin/questions" element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <AdminQuestions />
                  </ProtectedRoute>
                } />
                
                {/* Admin Industry Management - ADMIN ONLY */}
                <Route path="/admin/industry" element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <AdminIndustries />
                  </ProtectedRoute>
                } />
                
                {/* Admin Sub-Industry Management - ADMIN ONLY */}
                <Route path="/admin/subindustry" element={
                  <ProtectedRoute requiredRoles={['admin']}>
                    <AdminSubIndustries />
                  </ProtectedRoute>
                } />
                
               

                {/* Consultant Status Page */}
                <Route path="/consultant-status" element={<ConsultantStatusPage />} />

                {/* Protected Consultant Portal Routes - CONSULTANT/ADMIN ONLY */}
                <Route path="/consultant/dashboard" element={
                  <ProtectedRoute requiredRoles={['consultant', 'admin']}>
                    <ErrorBoundary componentName="ConsultantDashboard">
                      <ConsultantDashboard />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/consultant/session/:sessionId" element={
                  <ProtectedRoute requiredRoles={['consultant', 'admin']}>
                    <ErrorBoundary componentName="ConsultantSessionPage">
                      <ConsultantSessionPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/consultant/interview/:id" element={
                  <ProtectedRoute requiredRoles={['consultant', 'admin']}>
                    <ErrorBoundary componentName="ConsultantInterviewMode">
                      <ConsultantInterviewMode />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/consultant/branding" element={
                  <ProtectedRoute requiredRoles={['consultant', 'admin']}>
                    <ErrorBoundary componentName="ConsultantBrandingSettings">
                      <ConsultantBrandingSettings />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/consultant/client-reports" element={
                  <ProtectedRoute requiredRoles={['consultant', 'admin']}>
                    <ErrorBoundary componentName="ConsultantClientReports">
                      <ConsultantClientReports />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />
                <Route path="/consultant/results/:sessionId" element={
                  <ProtectedRoute requiredRoles={['consultant', 'admin']}>
                    <ConsultantResultsView />
                  </ProtectedRoute>
                } />
                <Route path="/consultant/training" element={
                  <ProtectedRoute requiredRoles={['consultant', 'admin']}>
                    <ConsultantTrainingHub />
                  </ProtectedRoute>
                } />

                {/* Additional Routes - PUBLIC */}
                <Route path="/comprehensive-report/:assessmentId" element={<ComprehensiveAssessmentReport />} />
              </Routes>
            </BrowserRouter>
          </div>
        </AppProvider>
      </AuthProvider>
      
    </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;